import { GetTelemetryHistoryResponse } from '@eppendorf/vnls-device-service-utils';
import { DotsLoader } from '@eppendorf/vnls-react-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { ChartLegend } from '$features/monitoring/history-charts-v2/components/Legend';
import { NoChartDataAvailable } from '$features/monitoring/history-charts-v2/components/no-data-available/no-chart-data-available';
import {
  HistoryChartV2,
  type HistoryChartV2Props,
} from '$features/monitoring/history-charts-v2/history-chart-v2';
import { useHistoryChartsConfig } from '$features/monitoring/history-charts-v2/useHistoryChartsConfig';
import { ParameterHeadline } from '$features/monitoring/shared/parameter-headline';

import styles from './history-chart-v2-wrapper.module.scss';

export interface HistoryChartV2WrapperProps extends Pick<
  HistoryChartV2Props,
  'syncId' | 'syncByTimestamp'
> {
  isLoading: boolean;
  isError: boolean;
  data?: GetTelemetryHistoryResponse;
  parameterName?: string;
  liveUpdate?: boolean;
  /** When true, setpoint and threshold lines are hidden in the chart */
  hideSetpoints?: boolean;
  /** Override fractional digits for chart values */
  fractionalDigits?: number;
}

export function HistoryChartV2Wrapper({
  isError,
  isLoading,
  data,
  parameterName,
  liveUpdate,
  hideSetpoints,
  fractionalDigits,
  syncId,
  syncByTimestamp,
}: HistoryChartV2WrapperProps) {
  const { t } = useTranslation();

  const { showThresholdRefs, chartColors } = useHistoryChartsConfig();

  // When hideSetpoints is true, override showThresholdRefs to false
  const effectiveShowThresholdRefs = hideSetpoints ? false : showThresholdRefs;

  const isProcessValuePresent = useMemo(
    () =>
      data?.historyData.some(
        (bucket) =>
          Number.isFinite(bucket.min) ||
          Number.isFinite(bucket.max) ||
          Number.isFinite(bucket.avg),
      ),
    [data],
  );

  const isSetpointPresent = useMemo(
    () => data?.historyData.some((bucket) => Number.isFinite(bucket.setPoint)),
    [data],
  );

  const isAlertLowPresent = useMemo(
    () => data?.historyData.some((bucket) => Number.isFinite(bucket.alertLow)),
    [data],
  );

  const isAlertHighPresent = useMemo(
    () => data?.historyData.some((bucket) => Number.isFinite(bucket.alertHigh)),
    [data],
  );

  const isAlertPresent = isAlertLowPresent || isAlertHighPresent;

  const isSomeDataPresent =
    isProcessValuePresent ||
    (effectiveShowThresholdRefs && (isSetpointPresent || isAlertPresent));

  return (
    <div
      className={styles.historyChartV2Wrapper}
      data-testid="chart-wrapper"
      key={data?.telemetryFunctionName}
    >
      <div className={styles.historyChartV2WrapperParameters}>
        <ParameterHeadline parameterName={parameterName} unit={data?.unit} />
        <ChartLegend>
          {isAlertPresent && effectiveShowThresholdRefs && (
            <ChartLegend.Parameter
              value={t('monitoringDetail.historyChartsDetail.highLowAlertIndicator')}
              color={chartColors.alertHighLowColor}
            />
          )}
          {isSetpointPresent && effectiveShowThresholdRefs && (
            <ChartLegend.Parameter
              value={t('monitoringDetail.historyChartsDetail.setPointIndicator')}
              color={chartColors.setPointColor}
            />
          )}
          {isProcessValuePresent && (
            <ChartLegend.Parameter
              value={t('monitoringDetail.historyChartsDetail.processValueIndicator')}
              color={chartColors.processValueColor}
            />
          )}
        </ChartLegend>
      </div>
      {isLoading && <DotsLoader className={styles.historyChartV2WrapperLoader} />}
      {isError && <LoadingErrorHint />}
      {data && !isSomeDataPresent && <NoChartDataAvailable />}
      {data && !isError && isSomeDataPresent && (
        <HistoryChartV2
          data={data}
          showLiveAnimation={liveUpdate}
          hideSetpoints={hideSetpoints}
          fractionalDigits={fractionalDigits}
          syncId={syncId}
          syncByTimestamp={syncByTimestamp}
        />
      )}
    </div>
  );
}
